import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiArrowUpRight, HiCodeBracket, HiXMark, HiPlayCircle,
  HiChevronLeft, HiChevronRight, HiPhoto,
} from 'react-icons/hi2';

import { stagger, fadeUp, viewport } from '../../utils/animations';

const DOCTOR_ON_CALL_IMAGES = [
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481042/doctorOnCall/ProjectSss/DoctorOnCall/wujw4fcpstehnehae0zk.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481038/doctorOnCall/ProjectSss/DoctorOnCall/fhbbpnmu235fxrbyzwyc.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481038/doctorOnCall/ProjectSss/DoctorOnCall/izgw5vad0wfgw8qmid0m.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481039/doctorOnCall/ProjectSss/DoctorOnCall/pswcc1gcefidavts2dhe.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481039/doctorOnCall/ProjectSss/DoctorOnCall/do3ujesozhnn3cwecgrl.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481041/doctorOnCall/ProjectSss/DoctorOnCall/rpawnr2g35c55maev3wk.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481041/doctorOnCall/ProjectSss/DoctorOnCall/opqlp5vbdxgwhdglumpo.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481041/doctorOnCall/ProjectSss/DoctorOnCall/sfsuq0193alzydpkjmba.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481042/doctorOnCall/ProjectSss/DoctorOnCall/kswkuyi7tmahrtx814tn.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481053/doctorOnCall/ProjectSss/DoctorOnCall/a5qnrs0udi4y5r1f2zkt.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481053/doctorOnCall/ProjectSss/DoctorOnCall/rniovqxc6ioiwkdxn3xe.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709481069/doctorOnCall/ProjectSss/DoctorOnCall/wim8o8zb33wf18bmjgu2.jpg?_s=public-apps',
];

const FIXMYPHONE_IMAGES = [
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1701016100/home_page_1_gjbopl.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1701016090/Dashboard_oaeh2j.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709570527/jnpwblvcuceexuboybcv.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709570510/xe4qqziphpgfkbdgmufo.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709570502/vvxnkhhwsi1hofh3ntgx.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1701016107/screencapture-fix-my-phone-ujjalzaman-vercel-app-blog-2023-11-25-22_02_02_h61aph.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1709699200/rettl8puju0p2uv9q9gc.jpg?_s=public-apps',
  'https://res.cloudinary.com/dg8dkpulv/image/upload/fl_preserve_transparency/v1701016094/appointment_wi8vvc.jpg?_s=public-apps',
];

const FALLBACK = [
  {
    id: 1,
    title: 'DoctorOnCall – Doctor Appointment Platform',
    category: 'Web App',
    desc: 'Full-stack healthcare platform for scheduling appointments with doctors. Patients can book visits, track status with a unique ID, and use role-based dashboards. Doctors manage treatments and prescriptions; automated email notifications and dynamic invoices included.',
    descriptionList: [
      'Dynamic homepage with doctor discovery and filtering',
      'Appointment booking with selectable dates and time ranges',
      'Role-based dashboards: Doctor (treatment, prescriptions) & Patient (appointments, progress)',
      'Email notifications for appointment confirmation and invoicing',
      'Appointment tracking via unique tracking ID from homepage',
      'Dynamic prescription and invoice generation',
      'User auth: sign-in, sign-up, password recovery, email verification (doctors)',
      'Industry-standard code: TypeScript backend, Prisma, code splitting',
    ],
    img: DOCTOR_ON_CALL_IMAGES[0],
    images: DOCTOR_ON_CALL_IMAGES,
    tech: ['React', 'Redux Toolkit', 'Ant Design', 'Node.js', 'Express', 'TypeScript', 'Prisma', 'PostgreSQL'],
    live: 'https://doctoroncall-bd.netlify.app/',
    code: 'https://github.com/Ujjalzaman/Doctor-Appointment',
    youtube: 'https://youtu.be/L6cgb7I-Ap4',
  },
  {
    id: 2,
    title: 'FixMyPhone – Mobile Repair Full-Stack',
    category: 'Web App',
    desc: 'One-stop Next.js + Express platform for mobile device repairs. Customers request repairs, book appointments, and pay via Stripe. Admins manage service requests, technicians, and payments; super admin handles role management.',
    descriptionList: [
      'Dynamic landing and admin/user dashboards',
      'Service request management: assign technicians, update status',
      'Appointment scheduling and technician management',
      'Stripe payment processing and verification',
      'Customer: registration, service request, tracking, order history',
      'Messaging and notifications for repair updates',
      'Reporting and analytics for performance and revenue',
    ],
    img: FIXMYPHONE_IMAGES[0],
    images: FIXMYPHONE_IMAGES,
    tech: ['Next.js', 'TypeScript', 'Express', 'Prisma', 'PostgreSQL', 'Stripe', 'Ant Design', 'Bootstrap'],
    live: 'https://fix-my-phone-ujjalzaman.vercel.app/',
    code: 'https://github.com/Ujjalzaman/Repair-mobile-nextjs-full-stack',
    youtube: 'https://youtu.be/IRuWuqt7yOs',
  },
  {
    id: 3,
    title: 'Easy Consulting – Agency Full-Stack',
    category: 'Web App',
    desc: 'MERN software agency website with eCommerce, blog, and business features. Responsive UI, user reviews, product purchase, Stripe payments. Admin and user dashboards; admin CRUD, order management, job portal; auth with OTP and email reset.',
    descriptionList: [
      'Responsive landing page and first-load optimized UI',
      'MUI and React Bootstrap components',
      'User reviews, product purchase, and order status',
      'Auth: login, signup, reset email, OTP, subscribe',
      'Stripe payment integration',
      'Separate dashboards for Admin and User',
      'Admin: CRUD, add admin, manage orders, full access',
      'Job portal and validated inputs across the site',
    ],
    img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&h=560&fit=crop',
    tech: ['React', 'JavaScript (ES6)', 'Firebase', 'Firestore', 'MUI', 'React Bootstrap', 'Stripe', 'Node.js', 'Express', 'MongoDB'],
    live: 'https://wonderful-kowalevski-8c905c.netlify.app/',
    code: 'https://github.com/Ujjalzaman/Easy-Consulting-react',
  },
];

const CATEGORIES = ['All', 'Web App'];

/* ── Helpers ─────────────────────────────────────────── */
function isVideo(url) {
  return typeof url === 'string' && (
    url.includes('youtube') || url.includes('youtu.be') || url.endsWith('.mp4') || url.endsWith('.webm')
  );
}
function ytId(url) {
  const m = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return m ? m[1] : null;
}

/* ── Lightbox ─────────────────────────────────────────── */
function Lightbox({ projects, startIdx, startWithVideo = false, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const [expanded, setExpanded] = useState(false);
  const [showVideo, setShowVideo] = useState(() => {
    const p = projects[startIdx];
    return Boolean(startWithVideo && p?.youtube);
  });
  const p = projects[idx];

  const prev = useCallback(() => { setIdx(i => Math.max(0, i - 1)); setExpanded(false); setShowVideo(false); }, []);
  const next = useCallback(() => { setIdx(i => Math.min(projects.length - 1, i + 1)); setExpanded(false); setShowVideo(false); }, [projects.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowLeft')   prev();
      if (e.key === 'ArrowRight')  next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose, prev, next]);

  const src = p.img || p.imgUrl || p.image || '';
  const images = p.images && p.images.length > 0 ? p.images : (src ? [src] : []);
  const [imgIdx, setImgIdx] = useState(0);
  const currentImg = images[imgIdx] || src;
  const isVid = isVideo(src);
  const vid = isVid ? ytId(src) : null;
  const desc = p.desc || p.description || '';
  const ytEmbedId = p.youtube ? ytId(p.youtube) : null;
  const showEmbed = showVideo && ytEmbedId;

  useEffect(() => { setImgIdx(0); }, [idx]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/88 backdrop-blur-xl p-4 sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 24 }}
        animate={{ scale: 1,    opacity: 1, y: 0  }}
        exit={{    scale: 0.88, opacity: 0, y: 24  }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        className="relative w-full max-w-4xl bg-ink-900 rounded-3xl overflow-hidden border border-white/[0.09] shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-xl bg-white/[0.07] hover:bg-white/[0.14] text-zinc-400 hover:text-white transition-all"
        >
          <HiXMark className="w-5 h-5" />
        </button>

        {/* Prev / Next */}
        {idx > 0 && (
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-2xl bg-white/[0.07] hover:bg-white/[0.14] text-zinc-400 hover:text-white transition-all">
            <HiChevronLeft className="w-5 h-5" />
          </button>
        )}
        {idx < projects.length - 1 && (
          <button onClick={next} className="absolute right-16 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-2xl bg-white/[0.07] hover:bg-white/[0.14] text-zinc-400 hover:text-white transition-all">
            <HiChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Media: embedded YouTube or image(s) */}
        <div className="relative aspect-video bg-ink-800 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div key={`${idx}-${showEmbed ? 'yt' : imgIdx}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.28 }} className="absolute inset-0">
              {showEmbed ? (
                <iframe
                  src={`https://www.youtube.com/embed/${ytEmbedId}?autoplay=1`}
                  title={p.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              ) : currentImg ? (
                <img src={currentImg} alt={p.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <HiPhoto className="w-16 h-16 text-zinc-700" />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          {!showEmbed && images.length > 1 && (
            <>
              {imgIdx > 0 && (
                <button type="button" onClick={() => setImgIdx(i => i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-xl bg-black/50 hover:bg-black/70 text-white">
                  <HiChevronLeft className="w-5 h-5" />
                </button>
              )}
              {imgIdx < images.length - 1 && (
                <button type="button" onClick={() => setImgIdx(i => i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-xl bg-black/50 hover:bg-black/70 text-white">
                  <HiChevronRight className="w-5 h-5" />
                </button>
              )}
            </>
          )}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {p.youtube && (
              <button
                type="button"
                onClick={() => setShowVideo(s => !s)}
                className="font-mono text-xs px-3 py-1.5 rounded-full border border-white/[0.2] bg-black/50 text-white hover:bg-red-600/90 hover:border-red-500 transition-colors"
              >
                {showEmbed ? 'Show image' : 'Watch video'}
              </button>
            )}
            <span className="font-mono text-xs text-zinc-600 px-3 py-1 glass rounded-full border border-white/[0.07]">
              {showEmbed ? 'Video' : `${imgIdx + 1} / ${images.length || 1}`}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-6 sm:p-7">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                <div>
                  {p.category && <span className="font-mono text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">{p.category}</span>}
                  <h3 className="font-display font-bold text-xl sm:text-2xl text-white">{p.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2 flex-shrink-0">
                  {p.youtube && (
                    <button
                      type="button"
                      onClick={() => setShowVideo(true)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-600/90 hover:bg-red-600 text-white text-sm font-semibold transition-colors"
                    >
                      <HiPlayCircle className="w-4 h-4" /> Watch video
                    </button>
                  )}
                  {p.code && p.code !== '#' && (
                    <a href={p.code} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl glass border border-white/[0.1] text-zinc-300 hover:text-white text-sm font-medium transition-colors"
                    >
                      <HiCodeBracket className="w-4 h-4" /> GitHub
                    </a>
                  )}
                  {p.live && p.live !== '#' && (
                    <a href={p.live} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition-colors"
                    >
                      Live <HiArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-1">
                {desc.length > 180 && !expanded ? `${desc.slice(0, 180)}…` : desc}
              </p>
              {desc.length > 180 && (
                <button onClick={() => setExpanded(e => !e)} className="font-mono text-xs text-indigo-400 hover:text-indigo-300 transition-colors mb-2">
                  {expanded ? 'Show less ↑' : 'Read more ↓'}
                </button>
              )}
              {p.descriptionList && p.descriptionList.length > 0 && (
                <ul className="text-sm text-zinc-500 leading-relaxed mb-3 list-disc list-inside space-y-1 marker:text-indigo-500/70">
                  {p.descriptionList.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p.tech || []).map(t => (
                  <span key={t} className="font-mono text-[10px] px-2.5 py-0.5 rounded-lg text-zinc-600 bg-white/[0.04] border border-white/[0.06]">{t}</span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Project Card ─────────────────────────────────────── */
function ProjectCard({ project: p, index, onImageClick, onWatchVideo }) {
  const [hovered, setHovered] = useState(false);
  const desc  = p.desc || p.description || '';
  const thumb = p.img || p.imgUrl || p.image;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 28, scale: 0.97 }}
      animate={{ opacity: 1, y: 0,  scale: 1    }}
      exit={{    opacity: 0, y: 16, scale: 0.97  }}
      transition={{ delay: index * 0.07, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="group flex flex-col glass rounded-3xl overflow-hidden border border-white/[0.06]
                 hover:border-indigo-500/25 hover:shadow-card-hover transition-all duration-300"
    >
      {/* ── Image — click to open modal ── */}
      <div
        className="relative h-48 overflow-hidden flex-shrink-0 cursor-zoom-in"
        onClick={() => onImageClick(index)}
      >
        {thumb ? (
          <motion.img
            src={thumb}
            alt={p.title}
            className="w-full h-full object-cover"
            animate={{ scale: hovered ? 1.08 : 1 }}
            transition={{ duration: 0.55 }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-ink-800">
            <HiPhoto className="w-10 h-10 text-zinc-700" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-transparent to-transparent" />

        {/* Category */}
        {p.category && (
          <span className="absolute top-3 left-3 font-mono text-[10px] font-bold px-2.5 py-1 rounded-full bg-indigo-600/80 text-white backdrop-blur-sm">
            {p.category}
          </span>
        )}

        {/* Hover: preview badge */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <span className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-black/60 backdrop-blur-md border border-white/20 text-white text-sm font-semibold">
            <HiPhoto className="w-4 h-4" /> Preview
          </span>
        </motion.div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-col flex-1 p-5 sm:p-6">
        <h3 className="font-display font-bold text-base sm:text-lg text-white leading-tight mb-2 group-hover:text-indigo-300 transition-colors">
          {p.title}
        </h3>
        {p.descriptionList && p.descriptionList.length > 0 ? (
          <ul className="text-sm text-zinc-500 leading-relaxed mb-4 flex-1 space-y-1 line-clamp-4 list-disc list-inside marker:text-indigo-500/70">
            {p.descriptionList.slice(0, 4).map((item, i) => (
              <li key={i} className="line-clamp-1">{item}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-zinc-500 leading-relaxed mb-4 line-clamp-2 flex-1">{desc}</p>
        )}

        <div className="flex flex-wrap gap-1.5 mb-5">
          {(p.tech || []).map(t => (
            <span key={t} className="font-mono text-[10px] px-2 py-0.5 rounded-lg text-zinc-600 bg-white/[0.03] border border-white/[0.05]">
              {t}
            </span>
          ))}
        </div>

        {/* ── Action buttons: Watch video (embedded), GitHub, Live ── */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-white/[0.05]">
          {p.youtube && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onWatchVideo?.(index); }}
              className="inline-flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-xl bg-red-600/80 hover:bg-red-600 text-white text-xs font-semibold transition-all duration-200 border border-red-500/40"
            >
              <HiPlayCircle className="w-3.5 h-3.5" /> Watch video
            </button>
          )}
          {p.code && (
            <a
              href={p.code} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-xl
                glass border border-white/[0.09] text-zinc-500 hover:text-white hover:border-white/20
                text-xs font-semibold transition-all duration-200"
            >
              <HiCodeBracket className="w-3.5 h-3.5" /> GitHub
            </a>
          )}
          {p.live && (
            <a
              href={p.live} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-xl
                bg-indigo-600/80 hover:bg-indigo-600 text-white
                text-xs font-semibold transition-all duration-200 border border-indigo-500/40"
            >
              <HiArrowUpRight className="w-3.5 h-3.5" /> Live Demo
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
}

/* ── Main Section ─────────────────────────────────────── */
export default function Projects() {
  const [projects,    setProjects]    = useState(FALLBACK);
  const [filter,      setFilter]      = useState('All');
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [lightboxStartWithVideo, setLightboxStartWithVideo] = useState(false);

  // useEffect(() => {
  //   FirebaseServices.getAllWebPreview()
  //     .then(snap => {
  //       const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  //       if (data?.length) setProjects(data);
  //     })
  //     .catch(() => {});
  // }, []);

  const filtered = filter === 'All' ? projects : projects.filter(p => p.category === filter);

  const openLightbox = (index, withVideo = false) => {
    setLightboxIdx(index);
    setLightboxStartWithVideo(withVideo);
  };
  const closeLightbox = () => {
    setLightboxIdx(null);
    setLightboxStartWithVideo(false);
  };

  return (
    <section id="projects" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 60% at 85% 20%, rgba(99,102,241,0.04) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        {/* Header */}
        <motion.div variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport} className="mb-12">
          <motion.div variants={fadeUp} className="section-label">
            <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">04 / Work</span>
          </motion.div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
            <motion.h2 variants={fadeUp} className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
              Selected <span className="gradient-text">projects</span>
            </motion.h2>
            <motion.a variants={fadeUp} href="https://github.com/Ujjalzaman" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-white transition-colors group"
            >
              All on GitHub <HiArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </motion.a>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)}
              className={`px-4 py-1.5 rounded-2xl text-sm font-semibold transition-all duration-200 ${
                filter === cat
                  ? 'bg-indigo-600 text-white shadow-glow-sm'
                  : 'glass text-zinc-600 hover:text-white border border-white/[0.06] hover:border-white/15'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Grid */}
        <motion.div layout className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <ProjectCard
                key={p.id ?? p.title}
                project={p}
                index={i}
                onImageClick={(idx) => openLightbox(idx, false)}
                onWatchVideo={(idx) => openLightbox(idx, true)}
              />
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.p variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
          className="mt-8 text-center font-mono text-xs text-zinc-800"
        >
          Hover image → click to open full preview
        </motion.p>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <Lightbox
            projects={filtered}
            startIdx={lightboxIdx}
            startWithVideo={lightboxStartWithVideo}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
